import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LoyaltyApi } from '../api';
import { useAuth } from '../context/AuthContext';
import { PageHero } from '../components/Common';

const fmt = (d) => new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

export default function RewardsPage() {
  const navigate = useNavigate();
  const { isLoggedIn, booting } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!booting && !isLoggedIn) navigate('/login', { replace: true });
  }, [booting, isLoggedIn, navigate]);

  const load = () => LoyaltyApi.me().then((d) => setData(d.loyalty)).catch((e) => setErr(e.message));
  useEffect(() => { if (isLoggedIn) load(); /* eslint-disable-next-line */ }, [isLoggedIn]);

  if (booting || (!data && !err)) {
    return <><PageHero title="My Rewards" /><section><div className="container"><p className="muted">Loading…</p></div></section></>;
  }
  if (err) {
    return <><PageHero title="My Rewards" /><section><div className="container"><p className="form-msg err">{err}</p></div></section></>;
  }

  const { points, milestones = [], nextMilestone, maxPoints, referralCode, referralCount, referralPoints, pointsPerRupee, referredUsers = [], openClaim, history = [] } = data;

  // Shareable link always points at this storefront's login with the code.
  const link = `${window.location.origin}/login?ref=${referralCode}`;

  const pct = maxPoints > 0 ? Math.min(100, Math.round((points / maxPoints) * 100)) : 0;

  const copy = () => {
    navigator.clipboard?.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const share = async () => {
    const text = `Join Golden Taj and shop premium basmati rice! Use my code ${referralCode}. ${link}`;
    if (navigator.share) {
      try { await navigator.share({ title: 'Golden Taj', text, url: link }); } catch (_) { /* cancelled */ }
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    }
  };

  const claim = async (mp) => {
    setBusy(true); setErr('');
    try { await LoyaltyApi.claim(mp); await load(); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <>
      <PageHero title="My Rewards" />
      <section>
        <div className="container rewards-layout">
          <div>
            {/* Points hero */}
            <div className="points-card">
              <div>
                <span className="points-label">Your Loyalty Points</span>
                <span className="points-value">{points.toLocaleString('en-IN')}</span>
                <span className="points-rate">Earn {pointsPerRupee} point{pointsPerRupee === 1 ? '' : 's'} per ₹1 spent</span>
              </div>
              <i className="fas fa-award points-icon" />
            </div>

            {/* Milestone progress */}
            <div className="panel-card">
              <h3>Redeem Milestones</h3>
              {nextMilestone ? (
                <p className="muted small">
                  {(nextMilestone.points - points).toLocaleString('en-IN')} points to <strong>{nextMilestone.title}</strong> — {nextMilestone.reward}
                </p>
              ) : (
                <p className="muted small">You've unlocked every reward tier. Amazing!</p>
              )}

              <div className="milestone-track">
                <div className="milestone-fill" style={{ width: `${pct}%` }} />
                {milestones.map((m) => {
                  const left = maxPoints > 0 ? Math.min(100, (m.points / maxPoints) * 100) : 0;
                  return (
                    <div key={m.points} className={`milestone-node ${m.reached ? 'reached' : ''}`} style={{ left: `${left}%` }}>
                      <span className="ms-dot"><i className={`fas ${m.reached ? 'fa-check' : 'fa-lock'}`} /></span>
                      <span className="ms-label">{m.title}<br />{m.points}</span>
                    </div>
                  );
                })}
              </div>

              {/* Claimable rewards */}
              <div className="reward-list">
                {milestones.map((m) => (
                  <div key={m.points} className={`reward-row ${m.reached ? 'reached' : ''}`}>
                    <div>
                      <strong>{m.title} · {m.points} pts</strong>
                      <span className="muted small">{m.reward}</span>
                    </div>
                    {openClaim && openClaim.milestonePoints === m.points ? (
                      <span className="claim-pending"><i className="fas fa-hourglass-half" /> Requested</span>
                    ) : m.reached ? (
                      <button className="btn-gold sm" disabled={busy || !!openClaim} onClick={() => claim(m.points)}>
                        {openClaim ? 'Claim in progress' : 'Claim'}
                      </button>
                    ) : (
                      <span className="muted small"><i className="fas fa-lock" /> Locked</span>
                    )}
                  </div>
                ))}
              </div>

              {openClaim ? (
                <p className="claim-note">
                  <i className="fas fa-circle-info" /> Your claim was submitted on {fmt(openClaim.at)}. Our team will contact you shortly.
                </p>
              ) : null}
            </div>

            {/* Points history */}
            {history.length ? (
              <div className="panel-card">
                <h3>Points History</h3>
                {history.map((h, i) => (
                  <div className="hist-row" key={i}>
                    <div>
                      <strong>{h.kind === 'referral' ? 'Referral bonus' : 'Order reward'}</strong>
                      <span className="muted small">{h.note} · {fmt(h.at)}</span>
                    </div>
                    <span className="ok">+{h.points}</span>
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          {/* Referral */}
          <aside className="bill-card">
            <h3><i className="fas fa-user-group" /> Refer &amp; Earn</h3>
            <p className="muted small">Share your code — you get <strong>{referralPoints} points</strong> for every friend who joins.</p>

            <div className="referral-code">{referralCode}</div>
            <div className="referral-actions">
              <button className="btn-gold sm" onClick={copy}>{copied ? 'Copied!' : 'Copy Link'}</button>
              <button className="btn-gold sm" onClick={share}><i className="fab fa-whatsapp" /> Share</button>
            </div>

            <div className="referral-stats">
              <div><b>{referralCount}</b><span>Friends Referred</span></div>
              <div><b>{referralCount * referralPoints}</b><span>Points Earned</span></div>
            </div>

            {referredUsers.length ? (
              <div className="referred-list">
                <span className="muted small">Your referrals</span>
                {referredUsers.map((u, i) => (
                  <div className="referred-row" key={i}>
                    <span>{u.name}</span>
                    <span className="muted small">{fmt(u.at)}</span>
                  </div>
                ))}
              </div>
            ) : null}

            <Link className="link-btn" to="/account" style={{ display: 'block', textAlign: 'center' }}>Back to Account</Link>
          </aside>
        </div>
      </section>
    </>
  );
}
